<template>
  <v-container class="pb-0">
    <v-card>
      <v-card-title>
        {{ $t("scheduleItemActivationDates") }}
        <v-spacer></v-spacer>
        <v-dialog v-model="dialogDelete" max-width="500px">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              class="ml-3"
              @click="dialogDelete = true"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
          <v-card tile>
            <v-card-title class="headline">{{
              $t("scheduleItemRemove")
            }}</v-card-title>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="green darken-1"
                text
                @click="deleteScheduleItem(), (dialogDelete = false)"
                >{{ $t("btnYes") }}</v-btn
              >
              <v-btn color="red darken-1" text @click="dialogDelete = false">{{
                $t("btnNo")
              }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card-title>
      <v-container class="py-0">
        <v-row justify="start" align="center" class="pb-2 px-2">
          <v-chip-group v-model="item.day_types" multiple column>
            <v-chip
              v-for="daytype in daytypes"
              :key="daytype.id"
              text-color="white"
              class="mx-1"
              filter
              label
              filter-icon="mdi-checkbox-marked-circle"
              :value="daytype.id"
              @click="changeAvailableDays(daytype.id)"
              :color="
                item.day_types.find((el) => el == daytype.id) &&
                !availableDays.find((el) => el == daytype.id)
                  ? 'green'
                  : 'blue'
              "
              :disabled="
                !item.day_types.find((el) => el == daytype.id) &&
                !availableDays.find((el) => el == daytype.id)
              "
            >
              {{ daytype.name }}
            </v-chip>
          </v-chip-group>
        </v-row>
      </v-container>
      <v-container v-show="item.day_types.length" class="pt-2 pb-0">
        <v-row justify="start" align="center">
          <v-text-field
            width="10"
            class="mx-4"
            type="time"
            v-model="item.begin"
            @blur="generateScheduleGrid(item)"
            :error-messages="itemBeginErrors"
            :label="$t('scheduleItemBegin') + '*'"
          >
          </v-text-field>
          <v-text-field
            class="mx-4"
            type="time"
            v-model="item.end"
            @blur="generateScheduleGrid(item)"
            :error-messages="itemEndErrors"
            :label="$t('scheduleItemEnd') + '*'"
          >
          </v-text-field>
          <v-text-field
            class="mx-4"
            type="number"
            v-model="item.duration"
            @blur="generateScheduleGrid(item)"
            :error-messages="itemDurationErrors"
            :label="$t('scheduleItemDuration') + '*'"
          >
          </v-text-field>
          <v-checkbox
            class="mx-4"
            v-model="item.overlap"
            color="success"
            @change="generateScheduleGrid(item)"
            :label="$t('scheduleItemNoRestrict')"
          >
          </v-checkbox>
        </v-row>
        <v-btn
          outlined
          color="green darken-1"
          text
          class="mt-2 mb-3 mr-4"
          tile
          :disabled="$v.$invalid"
          @click="updateScheduleInfo(schedule)"
          >{{ $t("scheduleItemSave") }}</v-btn
        >
        <small class="ml-2">* {{ $t("validation") }}</small>
      </v-container>
      <v-container
        v-show="item.begin && item.end && item.duration"
        class="pa-0"
      >
        <v-expansion-panels flat>
          <v-expansion-panel @click="generateScheduleGrid(item)">
            <v-expansion-panel-header class="justify-self-start pa-0">
              <v-card-title>
                {{ $t("scheduleItemGrid") }}
                <v-icon class="ml-4">mdi-hand-pointing-left</v-icon>
              </v-card-title>
              <template v-slot:actions>
                <v-icon v-show="false">$expand</v-icon>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="px-6">
              <v-row justify="start" align="center">
                <v-chip
                  v-for="(time, k) in scheduleGrid"
                  :key="k"
                  label
                  class="mx-1 mb-2"
                >
                  {{ time.time_start }}
                </v-chip>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-container>
      <v-row v-show="item.begin && item.end && item.duration">
        <v-col cols="12" sm="12" md="7" class="pt-0">
          <pricing-table
            :pricing="item.pricing"
            @onChange="updateScheduleInfo(schedule)"
          ></pricing-table>
        </v-col>
        <v-col cols="12" sm="12" md="5" class="pt-0">
          <breaks-table
            :breaks="item.breaks"
            @onChange="updateScheduleInfo(schedule)"
          ></breaks-table>
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import BreaksTable from "@/components/BreaksTable.vue";
import PricingTable from "@/components/PricingTable.vue";
import { validationMixin } from "vuelidate";
import { required, minValue } from "vuelidate/lib/validators";

export default {
  name: "ScheduleItem",

  mixins: [validationMixin],

  props: ["item", "item_id", "availableDays"],

  data() {
    return {
      dialogDelete: false,
      dialogCreateItem: false,
      daytypes: [
        {
          id: 1,
          name: this.$i18n.t("scheduleItemMonday"),
        },
        {
          id: 2,
          name: this.$i18n.t("scheduleItemTuesday"),
        },
        {
          id: 3,
          name: this.$i18n.t("scheduleItemWednesday"),
        },
        {
          id: 4,
          name: this.$i18n.t("scheduleItemThursday"),
        },
        {
          id: 5,
          name: this.$i18n.t("scheduleItemFriday"),
        },
        {
          id: 6,
          name: this.$i18n.t("scheduleItemSaturday"),
        },
        {
          id: 7,
          name: this.$i18n.t("scheduleItemSunday"),
        },
        {
          id: 8,
          name: this.$i18n.t("scheduleItemHoliday"),
        },
        {
          id: 9,
          name: this.$i18n.t("scheduleItemPreHoliday"),
        },
      ],
      scheduleGrid: [],
    };
  },

  validations: {
    item: {
      begin: {
        required,
      },
      end: {
        required,
      },
      duration: {
        required,
        minValue: minValue(1),
      },
    },
  },

  computed: {
    ...mapGetters(["schedule"]),
    itemBeginErrors() {
      const errors = [];
      if (!this.$v.item.begin.$dirty) return errors;
      !this.$v.item.begin.required &&
        errors.push(this.$i18n.t("validationRequired"));
      return errors;
    },
    itemEndErrors() {
      const errors = [];
      if (!this.$v.item.end.$dirty) return errors;
      !this.$v.item.end.required &&
        errors.push(this.$i18n.t("validationRequired"));
      return errors;
    },
    itemDurationErrors() {
      const errors = [];
      if (!this.$v.item.duration.$dirty) return errors;
      !this.$v.item.duration.required &&
        errors.push(this.$i18n.t("validationRequired"));
      return errors;
    },
  },

  components: {
    BreaksTable,
    PricingTable,
  },

  methods: {
    ...mapActions(["updateScheduleInfo", "checkSchedule"]),
    changeAvailableDays(day) {
      let self = this;
      if (day) {
        let exists = self.availableDays.find((el) => {
          return el == day;
        });
        let dayIndex = self.availableDays.indexOf(day);
        if (!exists) {
          self.availableDays.push(day);
        } else {
          self.availableDays.splice(dayIndex, 1);
        }
      } else {
        self.item.day_types.forEach((day, i) => {
          let exists = self.availableDays.find((el) => {
            return el == day;
          });
          let dayIndex = self.availableDays.indexOf(day);
          if (!exists) {
            self.availableDays.push(day);
          } else {
            self.availableDays.splice(dayIndex, 1);
          }
        });
      }
    },
    generateScheduleGrid(item) {
      let self = this;

      if (this.item.begin && this.item.end && this.item.duration) {
        this.$v.item.begin.$touch();
        this.$v.item.end.$touch();
        this.$v.item.duration.$touch();
      }

      if (!this.$v.$invalid) {
        self
          .checkSchedule(item)
          .then((response) => {
            if (response.data) {
              self.scheduleGrid = response.data;
            }
          })
          .catch((error) => {
            console.error(error);
          });
      }
    },
    deleteScheduleItem() {
      this.$delete(this.schedule.items, [this.item_id]);
      this.updateScheduleInfo(this.schedule);
      this.changeAvailableDays();
    },
  },

  mounted() {
    this.changeAvailableDays();
  },
};
</script>
